import { LoadingOutlined } from "@ant-design/icons";
import type React from "react";
import { useState } from "react";
import { useOptionalTransfer } from "../../TransferButton/TransferContext";

// Outbound-referral suggestion card: the referrals the note's plan (or the
// chat) asks for, offered for one-tap creation. A referral is a different
// record built by a different workflow than a lab/imaging/med order, so it
// gets its own card — never a row on the order card.
//
// Confirm dispatches `place_suggested_referrals` with the SELECTED referrals
// verbatim; the backend runs the referrals workflow directly and narrates
// progress into the thread. A refer-to pick is also sent the moment it is
// made (`referral_pick`), so it survives a chat correction's re-stage.
//
// When the note hasn't been transferred yet (note_transferred=false), the
// primary action is "Transfer note & create referrals": the existing transfer
// flow starts (banner / appointment / MFA modals unchanged), then
// `queue_referrals_after_transfer` stores them on the job — the backend
// creates them once the note lands, on the same encounter, even if the
// clinician navigates away. That order matters here: a referral attaches
// today's note, which has to be in the chart first.
//
// Corrections happen in chat ("make that urgent"): the backend re-emits this
// widget with the revised list and the older card renders FROZEN — the same
// list, greyed and non-interactive, with a caption saying what became of it.
// A card the clinician just acted on freezes the same way, from local `phase`
// alone: nothing in the thread disappears. Extra fields ride along untouched —
// the card's payload goes to the workflow verbatim, so this type stays open.
export interface SuggestedReferral {
  test_name: string;
  order_type?: string;
  specialty?: string;
  reason?: string;
  dx?: string;
  priority?: string;
  to_provider_name?: string;
  to_provider_id?: string;
  assigned_to_name?: string;
  assigned_to_id?: string;
  auth_required?: boolean;
  attach?: string[];
  // What the practice's convention (memory) says the MA still has to attach
  // by hand — the AI MA attaches today's note and nothing else.
  ma_attach?: string[];
  // The backend's own words for the destination and the owner ("To: Thomas,
  // Noel · practice default", "Owner: you"): rendered verbatim, so the row
  // always says who picks the specialist and where the referral lands.
  to_copy?: string;
  owner_copy?: string;
  to_source?: string;
  // The practice's refer-to doctors for this row, read from eCW when the card
  // was built (the destinations it has used, plus address-book matches for a
  // name the clinician spoke), ranked probable-first. The pick happens HERE,
  // in one click, the way the order card settles its catalogue item: the
  // chosen id rides out as `to_provider_id` and the backend checks it against
  // the options it persisted before creating anything. Absent on a card built
  // without a warm session — the row then renders as before and the create
  // run looks the name up itself.
  to_provider_options?: ReferralOption[];
  // Lines that need the clinician's attention (a payer that needs
  // authorization, an owner gap) — highlighted, never a reason to disable the
  // row.
  flags?: string[];
  deselected?: boolean;
}

export interface ReferralOption {
  value: string;
  label: string;
}

// The lines under a referral row:
//   "Routine · Palpitations, rule out AFib"
//   "Dx: I48.91, R00.2"
//   "To: Thomas, Noel · practice default"          (backend copy, verbatim)
//   "Owner: Nguyen,Tam"
//   "Attach: today's note"
//   "MA to attach: last ECG · lipid panel"
// Every fact the referral will carry is on the row, and each unresolved one
// says who resolves it — in the backend's own words (`to_copy`, `owner_copy`,
// `ma_attach`), which it writes on every row.
const TODAYS_NOTE = "today's note";

const referralDetail = (r: SuggestedReferral): string[] => {
  const priority = (r.priority || "").trim() || "routine";
  const lines = [
    [priority.charAt(0).toUpperCase() + priority.slice(1), (r.reason || "").trim()].filter(Boolean).join(" · "),
  ];
  const dx = (r.dx || "").trim();
  if (dx) lines.push(`Dx: ${dx}`);
  const to = (r.to_copy || "").trim();
  if (to) lines.push(to);
  const owner = (r.owner_copy || "").trim();
  if (owner) lines.push(owner);
  const byMa = (r.ma_attach || []).map((a) => (a || "").trim()).filter(Boolean);
  const byUs = (r.attach || []).map((a) => (a || "").trim()).filter((a) => a.toLowerCase() === TODAYS_NOTE);
  if (byUs.length) lines.push(`Attach: ${byUs.join(" · ")}`);
  if (byMa.length) lines.push(`MA to attach: ${byMa.join(" · ")}`);
  return lines;
};

export interface ReferralSuggestionsData {
  audio_file_id?: number | null;
  // Stable id for this card across revisions.
  card_id?: string;
  ehr_label?: string;
  question?: string;
  referrals?: SuggestedReferral[];
  blocked_reason?: string | null;
  note_transferred?: boolean;
  // "chat" cards are staged from dictation/chat with no note behind them —
  // there is nothing to transfer, so the combined action doesn't apply.
  source?: string;
  // Lifecycle, set by the backend on restore ("active" | "superseded" |
  // "placed" | "dismissed") plus a short human caption. Live-streamed cards
  // carry neither: the newest card in the thread is the active one.
  status?: string;
  resolution?: string | null;
}

interface ReferralSuggestionsProps {
  data: ReferralSuggestionsData;
  onCustomAction: (action: { type: string; payload?: Record<string, unknown> }) => void;
  disabled?: boolean;
  // This card is history: a newer card superseded it, or it was already
  // created/dismissed. Renders in full but greyed and inert — the thread must
  // still show what was offered and what happened to it.
  frozen?: boolean;
}

type Phase = "input" | "submitted" | "combined_submitted" | "dismissed";

// What a frozen card says happened to it. The backend's `resolution` wins (it
// carries the EHR's own words, e.g. "Created in eCW · Open · assigned to …");
// the status map covers cards persisted without one.
const STATUS_CAPTIONS: Record<string, string> = {
  placed: "Created",
  dismissed: "Dismissed",
  superseded: "Updated — see the newer card below.",
};

const frozenCaption = (data: ReferralSuggestionsData): string =>
  (data.resolution || "").trim() || STATUS_CAPTIONS[data.status || ""] || "Updated — see the newer card below.";

const ReferralSuggestions: React.FC<ReferralSuggestionsProps> = ({ data, onCustomAction, disabled, frozen }) => {
  const referrals = data?.referrals ?? [];
  const transfer = useOptionalTransfer();
  const [phase, setPhase] = useState<Phase>("input");
  const [isStartingTransfer, setIsStartingTransfer] = useState(false);
  // Checked by default — the card exists because the plan says to refer.
  // Keyed by index, not name: two referrals to the same specialty must not
  // collide.
  const [selected, setSelected] = useState<Set<number>>(
    () => new Set(referrals.map((_, i) => i).filter((i) => !referrals[i]?.deselected)),
  );
  // Destination picks by row index. A row the backend already routed starts
  // on that doctor; the clinician can change it or leave it blank for the MA
  // — a referral is never held back for lack of a pick, because the MA can
  // always set the specialist in eCW.
  const [picks, setPicks] = useState<Record<number, string>>(() => {
    const initial: Record<number, string> = {};
    referrals.forEach((r, i) => {
      if (r.to_provider_id && r.to_provider_options?.some((o) => o.value === r.to_provider_id)) {
        initial[i] = r.to_provider_id;
      }
    });
    return initial;
  });

  if (!referrals.length) return null;

  const blockedMissingMrn = data.blocked_reason === "missing_mrn";
  const noteTransferred = !!data.note_transferred;
  // A chat-staged card has no note behind it (audio_file_id null), so
  // "Transfer note & create…" would promise a transfer that can't happen.
  const canCombine = !noteTransferred && !!transfer && !!data.audio_file_id && data.source !== "chat";

  const toggle = (index: number) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });

  // A pick that differs from what the row came with rides out as the
  // referral's `to_provider_id` alone — the backend names it and marks it the
  // clinician's from its own copy of the card; a row whose pick was cleared
  // goes out with no destination so the MA chooses.
  const withPick = (r: SuggestedReferral, i: number): SuggestedReferral => {
    if (!r.to_provider_options?.length) return r;
    const pick = picks[i] ?? "";
    if (pick === (r.to_provider_id ?? "")) return r;
    const { to_provider_id, to_provider_name, to_source, to_copy, ...rest } = r;
    return pick ? { ...rest, to_provider_id: pick } : rest;
  };

  const selectedReferrals = () => referrals.map(withPick).filter((_, i) => selected.has(i));

  const create = () => {
    if (disabled || blockedMissingMrn || !selected.size) return;
    onCustomAction({
      type: "place_suggested_referrals",
      payload: {
        audio_file_id: data.audio_file_id,
        // The handler narrates "Working in eCW…" off this, as the order card does.
        ehr_label: data.ehr_label,
        card_id: data.card_id,
        referrals: selectedReferrals(),
      },
    });
    setPhase("submitted");
  };

  const transferAndCreate = async () => {
    if (disabled || blockedMissingMrn || !selected.size || !transfer || !data.audio_file_id || isStartingTransfer)
      return;
    setIsStartingTransfer(true);
    try {
      const result = await transfer.startTransfer(data.audio_file_id);
      if (result.jobId) {
        // A job id comes back for a fresh transfer, one already in flight
        // ("in_progress"), AND an already-completed one
        // ("already_transferred") — the backend chains the referrals behind
        // that job, creating them immediately when it's already done.
        onCustomAction({
          type: "queue_referrals_after_transfer",
          payload: {
            audio_file_id: data.audio_file_id,
            transfer_job_id: result.jobId,
            referrals: selectedReferrals(),
          },
        });
        // Don't promise a transfer that isn't happening: for a completed job
        // the referrals are being created right now.
        setPhase(result.code === "already_transferred" ? "submitted" : "combined_submitted");
      }
      // No job id means the transfer couldn't start; startTransfer already
      // surfaced the error toast and the card stays interactive for a retry.
    } finally {
      setIsStartingTransfer(false);
    }
  };

  const dismiss = () => {
    if (disabled) return;
    onCustomAction({ type: "referral_suggestions_dismiss" });
    setPhase("dismissed");
  };

  const ehrLabel = data.ehr_label || "your EHR";
  const busy = disabled || isStartingTransfer;
  // Acting on the card freezes it in place — the full list stays on screen,
  // greyed and inert, so the clinician can refer back to what was sent.
  const inert = !!frozen || phase !== "input";
  // A card the clinician acted on THIS session: the local outcome is truer
  // than anything persisted on the payload (which a poll may not have written
  // yet, or which a newer card froze under a different resolution).
  const localCaption =
    phase === "submitted"
      ? `Sent to ${ehrLabel} — I'll keep you posted in the chat.`
      : phase === "combined_submitted"
        ? "Transferring the note first, then creating these — you can move on."
        : phase === "dismissed"
          ? "Dismissed — nothing was created."
          : "";
  const count = selected.size;
  // Live count on the primary action ("Create 2 referrals") so partial
  // selections read back exactly what will happen; no count while nothing is
  // selected (the button is disabled then anyway).
  const countedReferrals = count ? `${count} referral${count === 1 ? "" : "s"}` : "referrals";
  const primaryDisabled = busy || blockedMissingMrn || !count;

  return (
    <div style={{ ...styles.container, ...(inert ? styles.frozenBox : {}) }}>
      <div style={styles.header}>
        <div style={{ ...styles.iconBadge, ...(inert ? styles.iconBadgeFrozen : {}) }}>
          <span style={styles.icon}>✦</span>
        </div>
        <span style={{ ...styles.headerText, ...(inert ? styles.frozenText : {}) }}>
          {data.question || `This note's plan includes a referral I can create in ${ehrLabel}:`}
        </span>
      </div>

      {blockedMissingMrn && !inert && (
        <div style={styles.blockedBanner}>
          Add this patient's {data.ehr_label || "eClinicalWorks"} account number to their chart to create referrals.
        </div>
      )}

      <div style={{ ...styles.referralList, ...(inert ? styles.referralListFrozen : {}) }}>
        {referrals.map((r, i) => {
          const checked = selected.has(i);
          const flags = (r.flags || []).map((f) => (f || "").trim()).filter(Boolean);
          // Frozen cards grey as a whole (box + list); rows that were left
          // unchecked still fade so a created card reads back accurately.
          const dimmed = !checked;
          return (
            <label
              key={`${r.test_name}#${i}`}
              style={{
                ...styles.referralRow,
                ...(i > 0 ? styles.referralRowDivider : {}),
                ...(busy || inert ? styles.referralRowBusy : {}),
              }}
            >
              <input
                type="checkbox"
                disabled={busy || inert}
                checked={checked}
                onChange={() => !inert && toggle(i)}
                style={styles.checkbox}
              />
              <span style={styles.referralTextCol}>
                <span style={{ ...styles.referralName, ...(dimmed ? styles.referralNameUnchecked : {}) }}>
                  {r.test_name}
                </span>
                {referralDetail(r).map((line, li) => (
                  <span
                    key={`${li}:${line}`}
                    style={{ ...styles.referralDetail, ...(dimmed ? styles.referralNameUnchecked : {}) }}
                  >
                    {line}
                  </span>
                ))}
                {(r.to_provider_options?.length ?? 0) > 0 && !inert && (
                  <span style={styles.pickRow}>
                    <span style={styles.pickArrow}>→</span>
                    <select
                      value={picks[i] ?? ""}
                      disabled={busy || !checked}
                      onChange={(e) => {
                        const value = e.target.value;
                        setPicks((prev) => {
                          const next = { ...prev };
                          if (value) {
                            next[i] = value;
                          } else {
                            delete next[i];
                          }
                          return next;
                        });
                        // Persisted on the server card now, not only on
                        // Create: a chat correction re-stages the card and
                        // carries a persisted pick across, while one held
                        // only here would be lost. Blank = cleared.
                        onCustomAction({
                          type: "referral_pick",
                          payload: {
                            card_id: data.card_id,
                            test_name: r.test_name,
                            specialty: r.specialty,
                            to_provider_id: value,
                          },
                        });
                      }}
                      aria-label={`Refer-to doctor for ${r.test_name}`}
                      style={{ ...styles.pickSelect, ...(picks[i] ? {} : styles.pickSelectEmpty) }}
                    >
                      <option value="">Leave the specialist for the MA to choose in {ehrLabel}…</option>
                      {r.to_provider_options?.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </span>
                )}
                {flags.map((flag, fi) => (
                  <span key={`${fi}:${flag}`} style={{ ...styles.flag, ...(dimmed ? styles.flagUnchecked : {}) }}>
                    {flag}
                  </span>
                ))}
              </span>
              <span style={styles.typeBadge}>Referral</span>
            </label>
          );
        })}
      </div>

      {inert ? (
        <div style={styles.captionRow}>
          {/* Only the combined action is still in flight when the card freezes. */}
          {phase === "combined_submitted" && (
            <LoadingOutlined spin style={{ color: "#9CA3AF", fontSize: 12, marginTop: 2 }} />
          )}
          <span style={styles.frozenCaption}>{localCaption || frozenCaption(data)}</span>
        </div>
      ) : (
        <>
          {/* The boundary, in one line: what this button does and what it
              leaves to the practice. */}
          <div style={styles.boundary}>
            Creates the referral in {ehrLabel} with today's note attached. Sending it stays with your staff.
          </div>
          <div style={styles.hint}>Need changes? Tell me below and I'll update this list.</div>

          <div style={styles.actions}>
            <button
              type="button"
              disabled={primaryDisabled}
              onClick={canCombine ? transferAndCreate : create}
              style={{ ...styles.primaryButton, ...(primaryDisabled ? styles.buttonDisabled : {}) }}
            >
              {isStartingTransfer
                ? "Starting…"
                : canCombine
                  ? `Transfer note & create ${countedReferrals}`
                  : `Create ${countedReferrals}`}
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={dismiss}
              style={{ ...styles.ghostButton, ...(busy ? styles.buttonDisabled : {}) }}
            >
              Not now
            </button>
          </div>
        </>
      )}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    padding: "14px",
    marginTop: "8px",
    borderRadius: "14px",
    border: "1px solid #E2E4F0",
    backgroundColor: "#FFFFFF",
    boxShadow: "0 1px 2px rgba(16, 24, 40, 0.05)",
    // Ancestors center-align text; every widget defends itself.
    textAlign: "left",
  },
  // Frozen = history: still fully readable, visibly not the live card.
  frozenBox: { backgroundColor: "#F9FAFB", border: "1px solid #E5E7EB", boxShadow: "none" },
  frozenText: { color: "#6B7280" },
  captionRow: { display: "flex", alignItems: "flex-start", gap: "7px" },
  frozenCaption: { fontSize: "12px", fontWeight: 500, color: "#9CA3AF", lineHeight: 1.4 },
  header: { display: "flex", alignItems: "flex-start", gap: "9px" },
  iconBadge: {
    width: "26px",
    height: "26px",
    borderRadius: "8px",
    backgroundColor: "#FFF7ED",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  iconBadgeFrozen: { backgroundColor: "#F3F4F6", filter: "grayscale(1)" },
  icon: { fontSize: "13px", lineHeight: 1, color: "#9A3412" },
  headerText: { fontSize: "13.5px", fontWeight: 600, color: "#111827", lineHeight: 1.45, paddingTop: "3px" },
  blockedBanner: {
    fontSize: "12.5px",
    fontWeight: 500,
    color: "#92400E",
    lineHeight: 1.45,
    backgroundColor: "#FFFBEB",
    border: "1px solid #FDE68A",
    borderRadius: "10px",
    padding: "9px 11px",
  },
  referralList: {
    display: "flex",
    flexDirection: "column",
    border: "1px solid #EEF0F4",
    borderRadius: "10px",
    backgroundColor: "#FBFBFD",
    overflow: "hidden",
  },
  referralListFrozen: { backgroundColor: "#F3F4F6", borderColor: "#E5E7EB" },
  referralRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: "10px",
    padding: "9px 12px",
    cursor: "pointer",
  },
  referralRowDivider: { borderTop: "1px solid #F0F1F5" },
  referralRowBusy: { cursor: "default" },
  checkbox: {
    width: "15px",
    height: "15px",
    marginTop: "2px",
    flexShrink: 0,
    accentColor: "#4F46E5",
    cursor: "inherit",
  },
  referralTextCol: { flex: 1, display: "flex", flexDirection: "column", gap: "2px", minWidth: 0 },
  referralName: { fontSize: "13px", fontWeight: 500, color: "#1F2937", lineHeight: 1.45, minWidth: 0 },
  referralDetail: { fontSize: "12px", fontWeight: 400, color: "#6B7280", lineHeight: 1.4 },
  referralNameUnchecked: { color: "#9CA3AF", fontWeight: 400 },
  // The inline destination pick, styled like the order card's catalogue pick.
  pickRow: { display: "flex", alignItems: "center", gap: "6px", marginTop: "2px", minWidth: 0 },
  pickArrow: { fontSize: "12px", color: "#9CA3AF", flexShrink: 0 },
  pickSelect: {
    flex: 1,
    minWidth: 0,
    maxWidth: "100%",
    fontSize: "12px",
    fontFamily: "inherit",
    color: "#1F2937",
    padding: "3px 6px",
    borderRadius: "7px",
    border: "1px solid #D1D5DB",
    backgroundColor: "#FFFFFF",
  },
  pickSelectEmpty: { color: "#6B7280", borderColor: "#C7D2FE", backgroundColor: "#F5F3FF" },
  // Unresolved referral lines: loud enough to read, never a blocker — the
  // checkbox stays live and the row goes out regardless.
  flag: {
    fontSize: "11.5px",
    fontWeight: 500,
    lineHeight: 1.4,
    color: "#B45309",
    backgroundColor: "#FFFBEB",
    border: "1px solid #FDE68A",
    borderRadius: "8px",
    padding: "4px 8px",
    marginTop: "2px",
  },
  flagUnchecked: { color: "#9CA3AF", backgroundColor: "#F9FAFB", borderColor: "#E5E7EB", fontWeight: 400 },
  typeBadge: {
    fontSize: "11px",
    fontWeight: 600,
    lineHeight: 1,
    padding: "4px 8px",
    borderRadius: "999px",
    whiteSpace: "nowrap",
    flexShrink: 0,
    marginTop: "1px",
    color: "#9A3412",
    backgroundColor: "#FFF7ED",
  },
  boundary: { fontSize: "12px", color: "#6B7280", lineHeight: 1.4 },
  hint: { fontSize: "12px", color: "#9CA3AF", lineHeight: 1.4 },
  actions: { display: "flex", gap: "8px", alignItems: "stretch" },
  primaryButton: {
    flex: 1,
    padding: "9px 14px",
    fontSize: "13px",
    fontWeight: 600,
    borderRadius: "10px",
    border: "1px solid #4F46E5",
    backgroundColor: "#4F46E5",
    color: "#FFFFFF",
    cursor: "pointer",
    fontFamily: "inherit",
    lineHeight: 1.4,
    boxSizing: "border-box",
  },
  ghostButton: {
    flex: "0 0 auto",
    padding: "8px 12px",
    fontSize: "13px",
    fontWeight: 500,
    borderRadius: "10px",
    border: "1px solid transparent",
    backgroundColor: "transparent",
    color: "#6B7280",
    cursor: "pointer",
    fontFamily: "inherit",
    lineHeight: 1.4,
    boxSizing: "border-box",
  },
  buttonDisabled: { opacity: 0.55, cursor: "not-allowed" },
};

export default ReferralSuggestions;
